"use strict";
/**
 * Схемы создания и изменения ProjectItem.
 *
 * Ключевое правило быстрого добавления: обязательны только type и title.
 * Всё остальное — включая обязательные свойства типа — можно заполнить позже.
 * Ошибка в необязательном поле не должна мешать зафиксировать информацию
 * (документ «Структура интерфейса», §9).
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.itemFilterSchema = exports.ITEM_SORT_FIELDS = exports.updateItemSchema = exports.createItemSchema = exports.itemWritableFieldsSchema = exports.quickAddItemSchema = void 0;
exports.validateItemAgainstRegistry = validateItemAgainstRegistry;
const contracts_1 = require("@project-os/contracts");
const zod_1 = require("zod");
const common_1 = require("./common");
const properties_1 = require("./properties");
const titleSchema = zod_1.z
    .string()
    .trim()
    .min(1, 'Укажите название')
    .max(500, 'Название не длиннее 500 символов');
/** Быстрое добавление: минимум полей, свойства типа проверяются мягко. */
exports.quickAddItemSchema = zod_1.z.object({
    type: zod_1.z.enum(contracts_1.ITEM_TYPES),
    title: titleSchema,
    bodyMd: common_1.markdownSchema.nullish(),
    workstreamId: common_1.uuidSchema.nullish(),
    ownerId: common_1.uuidSchema.nullish(),
    properties: zod_1.z.record(zod_1.z.string(), zod_1.z.unknown()).default({}),
    detail: zod_1.z.record(zod_1.z.string(), zod_1.z.unknown()).nullish(),
});
/** Полное создание или изменение элемента. */
exports.itemWritableFieldsSchema = zod_1.z.object({
    title: titleSchema,
    bodyMd: common_1.markdownSchema.nullish(),
    status: zod_1.z.string().max(40).nullish(),
    priority: zod_1.z.enum(contracts_1.PRIORITIES).nullish(),
    workstreamId: common_1.uuidSchema.nullish(),
    ownerId: common_1.uuidSchema.nullish(),
    dueDate: common_1.isoDateSchema.nullish(),
    isNextAction: zod_1.z.boolean().optional(),
    properties: zod_1.z.record(zod_1.z.string(), zod_1.z.unknown()).optional(),
    detail: zod_1.z.record(zod_1.z.string(), zod_1.z.unknown()).nullish(),
});
exports.createItemSchema = exports.itemWritableFieldsSchema.extend({
    type: zod_1.z.enum(contracts_1.ITEM_TYPES),
});
exports.updateItemSchema = exports.itemWritableFieldsSchema.partial();
/**
 * Значение фильтра из строки запроса.
 * Повторяющийся параметр (`?type=TASK&type=RISK`) приходит массивом,
 * одиночный — строкой; поддерживается и запись через запятую.
 */
function queryList(values) {
    return zod_1.z.preprocess((raw) => {
        if (raw === undefined || raw === null || raw === '')
            return undefined;
        const list = Array.isArray(raw) ? raw : String(raw).split(',');
        return list.map((value) => String(value).trim()).filter(Boolean);
    }, zod_1.z.array(zod_1.z.enum(values)).optional());
}
function queryUuidList() {
    return zod_1.z.preprocess((raw) => {
        if (raw === undefined || raw === null || raw === '')
            return undefined;
        const list = Array.isArray(raw) ? raw : String(raw).split(',');
        return list.map((value) => String(value).trim()).filter(Boolean);
    }, zod_1.z.array(common_1.uuidSchema).optional());
}
const queryBoolean = zod_1.z.preprocess((raw) => {
    if (raw === undefined || raw === null || raw === '')
        return undefined;
    if (typeof raw === 'boolean')
        return raw;
    return String(raw).toLowerCase() === 'true';
}, zod_1.z.boolean().optional());
exports.ITEM_SORT_FIELDS = [
    'updatedAt',
    'createdAt',
    'title',
    'dueDate',
    'priority',
    'status',
];
/** Фильтры и сортировка страницы «Элементы». */
exports.itemFilterSchema = zod_1.z.object({
    type: queryList(contracts_1.ITEM_TYPES),
    status: queryList(contracts_1.ALL_ITEM_STATUSES),
    workstreamId: queryUuidList(),
    ownerId: queryUuidList(),
    createdById: queryUuidList(),
    priority: queryList(contracts_1.PRIORITIES),
    isNextAction: queryBoolean,
    /** Только незавершённые элементы. */
    openOnly: queryBoolean,
    dueBefore: common_1.isoDateSchema.optional(),
    dueAfter: common_1.isoDateSchema.optional(),
    search: zod_1.z.string().trim().max(300).optional(),
    includeArchived: zod_1.z.preprocess((raw) => (typeof raw === 'string' ? raw.toLowerCase() === 'true' : raw), zod_1.z.boolean().default(false)),
    sort: zod_1.z.enum(exports.ITEM_SORT_FIELDS).default('updatedAt'),
    direction: zod_1.z.enum(['asc', 'desc']).default('desc'),
    cursor: zod_1.z.string().max(200).optional(),
    limit: zod_1.z.coerce.number().int().min(1).max(200).default(50),
});
/**
 * Проверяет согласованность элемента с реестром: допустимость статуса для типа,
 * применимость приоритета, срока и флага «следующее действие», корректность
 * свойств типа. Возвращает все нарушения сразу, а не первое.
 */
function validateItemAgainstRegistry(type, input, mode = 'full') {
    const definition = (0, contracts_1.getItemTypeDefinition)(type);
    const issues = [];
    const resolvedStatus = input.status ?? (0, contracts_1.getInitialStatus)(type);
    if (!(0, contracts_1.isValidStatusForType)(type, resolvedStatus)) {
        const allowed = definition.statuses.map((status) => status.value).join(', ');
        issues.push({
            field: 'status',
            message: `Статус «${resolvedStatus}» недопустим для типа «${definition.labelRu}». Допустимые: ${allowed}`,
        });
    }
    if (input.priority && !definition.supportsPriority) {
        issues.push({
            field: 'priority',
            message: `Приоритет не применяется к типу «${definition.labelRu}»`,
        });
    }
    if (input.dueDate && !definition.supportsDueDate) {
        issues.push({
            field: 'dueDate',
            message: `Срок не применяется к типу «${definition.labelRu}»`,
        });
    }
    if (input.isNextAction === true && !definition.supportsNextAction) {
        issues.push({
            field: 'isNextAction',
            message: `Тип «${definition.labelRu}» не может быть следующим действием`,
        });
    }
    if (input.properties !== undefined) {
        const result = (0, properties_1.getPropertiesSchema)(type, mode).safeParse(input.properties ?? {});
        if (!result.success) {
            for (const [field, message] of Object.entries((0, properties_1.toFieldErrors)(result.error))) {
                issues.push({ field: `properties.${field}`, message });
            }
        }
    }
    const detailSchema = (0, properties_1.getDetailSchema)(type, mode);
    if (detailSchema && input.detail !== undefined && input.detail !== null) {
        const result = detailSchema.safeParse(input.detail);
        if (!result.success) {
            for (const [field, message] of Object.entries((0, properties_1.toFieldErrors)(result.error))) {
                issues.push({ field: `detail.${field}`, message });
            }
        }
    }
    const statusDefinition = definition.statuses.find((status) => status.value === resolvedStatus);
    return {
        ok: issues.length === 0,
        issues,
        resolvedStatus,
        isTerminal: statusDefinition?.isTerminal === true,
    };
}
//# sourceMappingURL=items.js.map